import { type ReactNode } from "react";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useChatChannelInstallLink, useChatChannels } from "@/hooks/agent/useChatChannels";
import { Agent } from "@/types/agent/agent";
import { ChatCode, CheatSheetCard, PRIMARY_BUTTON, SectionHeading, WorkspaceRow } from "./ChatChannelShared";
import SlackOwnAppSection from "./SlackOwnAppSection";

interface SlackAccessSectionProps {
  genie: Agent;
}

export default function SlackAccessSection({ genie }: SlackAccessSectionProps) {
  const { data: installs = [], isLoading } = useChatChannels(genie.id);
  const { data: installLink, isLoading: isLinkLoading } = useChatChannelInstallLink(genie.id, "slack");
  const slackInstalls = installs.filter((install) => install.platform === "slack");

  const handleCopyLink = async () => {
    if (!installLink) return;
    try {
      await navigator.clipboard.writeText(installLink);
      toast.success("Install link copied — send it to your customer's Slack admin");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleOpenLink = () => {
    if (installLink) window.open(installLink, "_blank", "noopener,noreferrer");
  };

  const copyIcon = isLinkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />;

  return (
    <div className="pt-4 space-y-6">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      ) : slackInstalls.length === 0 ? (
        <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[10px] bg-white p-5">
            <p className="font-semibold text-gray-900">Answer customers in their Slack</p>
            <p className="text-sm text-gray-600 mt-1">
              Send this link to a customer, or open it to add {genie.name} to your own Slack. Workspaces appear here as
              soon as it's added.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleCopyLink}
                disabled={!installLink}
                className={PRIMARY_BUTTON}
              >
                {copyIcon}
                Copy Slack install link
              </Button>
              <Button
                variant="outline"
                onClick={handleOpenLink}
                disabled={!installLink}
                className="min-h-[44px] font-semibold px-6"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in Slack
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <SectionHeading>Connected workspaces ({slackInstalls.length})</SectionHeading>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button size="sm" onClick={handleCopyLink} disabled={!installLink} className={PRIMARY_BUTTON}>
                {copyIcon}
                Copy Slack install link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenLink}
                disabled={!installLink}
                className="min-h-[44px] font-semibold"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in Slack
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            {slackInstalls.map((install) => (
              <WorkspaceRow
                key={install.id}
                agentId={genie.id}
                install={install}
                platformLabel="Slack"
                fallbackName="Slack workspace"
              />
            ))}
          </div>
        </div>
      )}

      <SlackCheatSheet genieName={genie.name} />

      <SlackOwnAppSection genie={genie} />
    </div>
  );
}

function SlackCheatSheet({ genieName }: { genieName: string }) {
  const cards: Array<{ title: string; body: ReactNode; className?: string }> = [
    {
      title: "Direct message",
      body: (
        <>
          Open <strong>Help Genie</strong> under <em>Apps</em> in Slack's sidebar and ask anything.
        </>
      ),
    },
    {
      title: "In a channel",
      body: (
        <>
          Type <ChatCode>/invite @Help Genie</ChatCode> once. Then start a message with{" "}
          <ChatCode>{genieName}, what can you do?</ChatCode> or mention <ChatCode>@Help Genie</ChatCode>. It answers
          in a thread, and follow-ups there need no name.
        </>
      ),
      className: "sm:row-span-2",
    },
    {
      title: "Several genies",
      body: (
        <>
          Slack asks "Who should answer?" when it isn't clear. <ChatCode>/helpgenie</ChatCode> lists them, and{" "}
          <ChatCode>/helpgenie {genieName}</ChatCode> switches to {genieName}.
        </>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3">
        <SectionHeading>Talking to {genieName} in Slack</SectionHeading>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((card) => (
          <CheatSheetCard key={card.title} title={card.title} className={card.className}>
            {card.body}
          </CheatSheetCard>
        ))}
      </div>
    </div>
  );
}
